// The ASO.Market login the whole panel orders with. Super admins only: the
// password is stored encrypted in the worker and never comes back.
import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/Button';
import { useToastStore } from '@/store/toast';
import { saveAccount, type AsomEnv, type AsomOverview } from '@/api/asoMarket';
import { Card, FieldLabel, INPUT, Notice, Segmented, usd } from './ui';

export function AccountPanel({ overview }: { overview: AsomOverview }) {
  const qc = useQueryClient();
  const toast = useToastStore((s) => s.push);
  const a = overview.account;
  const [env, setEnv] = useState<AsomEnv>(a.env);
  const [email, setEmail] = useState(a.email);
  const [password, setPassword] = useState('');
  const [cap, setCap] = useState(String(a.max_cost));
  useEffect(() => { setEnv(a.env); setEmail(a.email); setCap(String(a.max_cost)); }, [a.env, a.email, a.max_cost]);

  const save = useMutation({
    mutationFn: () => saveAccount({ env, email, password: password || undefined, max_cost: Number(cap) }),
    onSuccess: (d) => {
      setPassword('');
      toast(`Connected as ${d.name}: balance ${usd(d.balance)}`);
      qc.invalidateQueries({ queryKey: ['aso-market'] });
    },
    onError: (e: Error) => toast(e.message),
  });
  const envChanged = env !== a.env && a.email !== '';

  return (
    <Card className="p-5 max-w-xl space-y-4">
      <div>
        <div className="text-sm font-medium">ASO.Market account</div>
        <p className="text-xs text-content-subtle mt-0.5">Every order from the panel is placed with this login. The password is stored encrypted and never shown again.</p>
      </div>
      {!a.key_configured && <Notice tone="danger">The worker has no FACTORY_CRED_KEY secret: the password cannot be stored.</Notice>}
      {overview.error && <Notice tone="danger">{overview.error}</Notice>}
      {overview.connected && overview.profile && (
        <Notice tone="success">Connected as {overview.profile.name} ({overview.profile.email}) · balance {usd(overview.profile.balance)}</Notice>
      )}
      <div>
        <FieldLabel>Environment</FieldLabel>
        <Segmented value={env} onChange={setEnv} options={[
          { value: 'dev', label: 'Dev stand', hint: 'Test money, shared stand' },
          { value: 'prod', label: 'Production', hint: 'Real money, real store' },
        ]} />
        {envChanged && <p className="mt-1.5 text-xs text-warning">Switching stands needs that stand’s own login; campaigns of the other stand stay listed under it.</p>}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <FieldLabel>Email</FieldLabel>
          <input className={INPUT} type="email" autoComplete="off" value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <div>
          <FieldLabel>Password</FieldLabel>
          <input className={INPUT} type="password" autoComplete="new-password" placeholder={a.has_password ? 'unchanged' : ''} value={password} onChange={(e) => setPassword(e.target.value)} />
        </div>
      </div>
      <div className="max-w-[220px]">
        <FieldLabel aside="USD">Order cap</FieldLabel>
        <input className={INPUT} type="number" min={1} value={cap} onChange={(e) => setCap(e.target.value)} />
        <p className="mt-1 text-xs text-content-subtle">Orders above it need a super admin.</p>
      </div>
      <Button loading={save.isPending} disabled={!email || (!a.has_password && !password) || !(Number(cap) > 0)} onClick={() => save.mutate()}>
        Save and test login
      </Button>
    </Card>
  );
}
